import { createColumnHelper } from '@tanstack/react-table';
import { StatusPill } from 'component-library';
import { User } from '../models/ApiModels';

type UserList = {
  id?: string;
  azureObjectId?: string;
  displayName?: string;
  oemTenantId?: string;
  roles?: string;
  email?: string;
  active?: boolean;
};

export function GetUserListData(users: User[]) {
  const userList: UserList[] = users.map((user: User) => {
    return {
      id: user.id,
      displayName: user.displayName,
      oemTenantId: user.oemTenantId,
      roles: user.roles,
      email: user.email,
      active: user.active,
    };
  });

  return userList;
}

// Role status pill helpers
const parseRoles = (rolesString?: string): string[] => {
  if (!rolesString) return [];
  return rolesString.split(',').filter(Boolean);
};

const hasMainRole = (roles: string[]): boolean => {
  return roles.includes('admin') || roles.includes('editor');
};

const formatRoleDisplayText = (role: string): string => {
  const roleDisplayMap: Record<string, string> = {
    presetCertified: 'Preset Certified',
    softwareManager: 'Software Manager',
    admin: 'Admin',
    editor: 'Editor',
  };

  return roleDisplayMap[role] || role.charAt(0).toUpperCase() + role.slice(1);
};

const getRolePillColor = (role: string): 'Light Blue' | 'Dark Gray' | 'Light Gray' => {
  if (role === 'presetCertified') return 'Light Blue';
  if (role === 'admin' || role === 'softwareManager') return 'Dark Gray';
  return 'Light Gray';
};

const columnHelper = createColumnHelper<UserList>();

export const userListColumns = [
  columnHelper.accessor('displayName', {
    header: 'Name',
    cell: (info) => <div className='truncate'>{info.getValue()}</div>,
  }),
  columnHelper.accessor('email', {
    header: 'Email Address',
    cell: (info) => <div className='truncate'>{info.getValue()}</div>,
    size: 300,
  }),
  columnHelper.accessor('active', {
    header: 'Status',
    cell: (info) =>
      info.getValue() === true ? (
        <StatusPill color='Green' text='Active' />
      ) : (
        <StatusPill color='Dark Gray' text='Invited' />
      ),
    size: 100,
  }),
  columnHelper.accessor('roles', {
    header: 'Roles',
    cell: (info) => {
      const roles = parseRoles(info.getValue());
      const showViewer = !hasMainRole(roles);

      return (
        <div className='2xl:w-full xl:w-72 flex flex-wrap flex-row gap-2 p-2 pl-0'>
          {showViewer && <StatusPill text='Viewer' color='Light Gray' />}
          {roles.map((role, index) => (
            <StatusPill key={index} text={formatRoleDisplayText(role)} color={getRolePillColor(role)} />
          ))}
        </div>
      );
    },
    size: 280,
  }),
];
